import { CheckInMetricsData } from '@linka/domain/dtos';
import { CheckInType } from '@linka/domain/enums';
import { eventDashboardQueries } from '@linka/http/event-dashboard';
import { useTranslation } from '@linka/i18n';
import { formatDate } from '@linka/utils';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router';
import { ColumnDef, DataTable } from '../../../data-table/data-table';
import { Breadcrumb } from '../breadcrumb';
import { ExportChartTriggerButton } from '../components';
import { ExportFlowWrapper } from '../export-flow-wrapper';
import { DashboardFilters, DashboardFiltersDatePicker, dashboardFiltersAtom } from '../filters';
import { useSearchFilters } from '../hooks/use-search-filters';
import { useSystemFilters } from '../hooks/use-system-filters';

interface DetailedCheckinsProps {
  userEmail?: string;
}

export const DetailedCheckins = ({ userEmail }: DetailedCheckinsProps) => {
  const t = useTranslation();
  const { eventId = '' } = useParams();
  const { searchParams, setFilter } = useSearchFilters();
  const systemFilters = useSystemFilters();
  const dashboardFilters = useAtomValue(dashboardFiltersAtom);
  const type = (searchParams.get('type') as CheckInType) || 'general';
  const { data: checkins, isPending } = useQuery(
    eventDashboardQueries.checkins(eventId, {
      startDate: dashboardFilters.startDate,
      endDate: dashboardFilters.endDate,
      type,
      ...systemFilters,
    }),
  );

  const columns: ColumnDef<CheckInMetricsData>[] = [
    {
      accessorKey: 'activityName',
      header: t('dashboard.detailed.activity-name'),
    },
    {
      accessorKey: 'participantId',
      header: t('dashboard.detailed.participant-id'),
    },
    {
      accessorKey: 'participantName',
      header: t('dashboard.detailed.participant-name'),
    },
    {
      accessorKey: 'participantEmail',
      header: t('dashboard.detailed.participant-email'),
    },
    {
      accessorKey: 'participantPhone',
      header: t('dashboard.detailed.participant-phone'),
    },
    {
      accessorKey: 'ticketTitle',
      header: t('ticket'),
    },
    {
      accessorKey: 'tagNumber',
      header: t('dashboard.detailed.tag-number'),
    },
    {
      accessorKey: 'checkinDate',
      header: t('dashboard.detailed.checkin-date'),
      cell: ({ row }) => <span>{formatDate({ date: row.original.checkinDate, dateFormat: 'dd/MM/yyyy HH:mm' })}</span>,
    },
    {
      accessorKey: 'checkInUserEmail',
      header: t('dashboard.detailed.checkin-operator-email'),
    },
  ];

  return (
    <div>
      <Breadcrumb hideHome />
      <header className='grid sm:grid-cols-[1fr,max-content] items-center gap-y-mini my-xxs'>
        <h1 className='ts-heading-sm sm:ts-heading-md text-ctx-content-title'>
          {t('dashboard.detailed.participants-biped')}
        </h1>
        <div className='ml-auto sm:ml-0 sm:mr-auto sm:col-start-1'>
          <DashboardFilters>
            <DashboardFiltersDatePicker />
          </DashboardFilters>
        </div>
        <ExportChartTriggerButton type='CHECKIN' className='col-span-2 self-end sm:col-span-1' />
      </header>
      <DataTable
        isPending={isPending}
        columns={columns}
        perPage={10}
        total={checkins?.total}
        data={checkins?.data || []}
        orderBy={systemFilters.orderBy}
        sortOrder={systemFilters.sortOrder}
        onSortChange={(col, dir) => setFilter({ orderBy: col, sortOrder: dir })}
        page={systemFilters.page}
        onPageChange={(page) => setFilter({ page: page.toString() })}
      />
      <ExportFlowWrapper userEmail={userEmail} />
    </div>
  );
};
